/* Frengee owns stock/rules; Terrain owns presentation. See LOCAL-IMPLEMENTATION.md. */
(() => {
  if (customElements.get('bikezone-availability')) return;
  const normalize = value => String(value || '').trim().toLowerCase();
  const special = name => normalize(name).includes('special order');
  const copy = (root, selector, text) => { root.querySelector(selector).textContent = text; };
  let sequence = 0, opened = null, escapeRelease = false;

  class Availability extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.request = (this.request || 0) + 1;
      this.configured = false;
      this.section = this.closest('[data-section-type="product"]');
      this.summary = this.querySelector('[data-pickup-summary]');
      this.drawer = this.querySelector('.bikezone-availability-drawer');
      this.overlay = this.querySelector('.bikezone-availability-overlay');
      this.trigger = this.querySelector('[data-pickup-open]');
      this.rows = this.drawer.querySelector('[data-pickup-rows]');
      this.image = this.drawer.querySelector('img');
      this.defaultImage = this.image?.getAttribute('src');
      const id = 'BikeZoneAvailability-' + (++sequence);
      this.drawer.id = id;
      const title = this.drawer.querySelector('[data-availability-product-title]');
      title.id = id + '-title';
      this.drawer.setAttribute('aria-labelledby', title.id);
      this.trigger.setAttribute('aria-controls', id);
      this.trigger.href = '#' + id;
      this.trigger.onclick = event => { event.preventDefault(); this.open(); };
      this.drawer.querySelector('[data-pickup-close]').onclick = () => this.close();
      this.overlay.onclick = () => this.close();
      document.body.append(this.drawer, this.overlay);
      this.onVariant = event => this.update(event.detail.variant);
      this.section?.addEventListener('variantChange', this.onVariant);
      try {
        // Only this inert fragment knows vendor selectors. Never activate its UI/assets.
        const config = this.querySelector('[data-pickup-config]').content.querySelector('[data-frengee-pickup-block]');
        this.options = config.dataset;
        this.addresses = JSON.parse(this.querySelector('[data-pickup-addresses]')?.textContent || '{}');
        const matrix = JSON.parse(config.querySelector('[data-frengee-transfer-matrix]').textContent);
        this.stores = Array.isArray(matrix) ? matrix : matrix.stores;
        if (!Array.isArray(this.stores) || !this.stores.length || this.stores.some(store =>
          !store || typeof store.name_match !== 'string' || !store.name_match.trim() ||
          typeof store.pickup_enabled !== 'boolean' || (store.sources && !Array.isArray(store.sources)))) throw Error('Invalid rules');
        if (new Set(this.stores.map(store => normalize(store.name_match))).size !== this.stores.length) throw Error('Ambiguous rules');
        this.url = new URL(this.options.inventoryProxyUrl, location.origin);
        if (!this.options.inventoryProxyUrl || this.url.origin !== location.origin) throw Error('Invalid endpoint');
        this.order = JSON.parse(this.querySelector('[data-pickup-order]').textContent).map(normalize);
        const variant = JSON.parse(this.section.querySelector('[data-current-variant-json]').textContent);
        this.configured = true;
        this.update(variant);
      } catch (_) {
        this.message('Availability unavailable. Please contact the store.');
      }
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.close();
      this.request++;
      this.controller?.abort();
      this.section?.removeEventListener('variantChange', this.onVariant);
      // Retain owned markup for reconnecting the same element (AJAX/editor moves).
      this.append(this.drawer, this.overlay);
      this.ready = false;
    }

    message(text) {
      this.summary.textContent = text;
      this.rows.textContent = text;
    }

    clone(selector) { return this.querySelector(selector).content.firstElementChild.cloneNode(true); }

    render(locations) {
      const find = name => {
        const matches = locations.filter(item => item && normalize(item.name) === normalize(name));
        // Frengee treats omitted locations in a populated inventory response as out of stock.
        if (!matches.length && locations.length) return { name: String(name).trim().replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1)), available: false };
        return matches.length === 1 && typeof matches[0].available === 'boolean' ? matches[0] : null;
      };
      // Keep physical stores in a fixed order regardless of variant inventory ordering.
      const rank = name => {
        const key = normalize(name);
        if (key === 'the bike zone') return 0;
        if (key === 'the bike zone barrie') return 1;
        const i = this.order.indexOf(key);
        return 2 + (i < 0 ? this.order.length : i);
      };
      const stores = this.stores.filter(store => store.show_in_pickup_list !== false).slice().sort((a,b) =>
        Number(special(a.name_match)) - Number(special(b.name_match)) || rank(a.name_match) - rank(b.name_match));
      if (!stores.length) { this.message('Availability unavailable. No locations are configured.'); return; }
      this.summary.replaceChildren();
      this.rows.replaceChildren();
      let unknown = false, transfer = false;
      for (const store of stores) {
        const local = find(store.name_match);
        const sources = (store.sources || []).filter(source => source && source.enabled !== false);
        // A confirmed source can supply pickup even when destination stock is unknown.
        const source = store.pickup_enabled && local?.available !== true
          ? sources.find(source => find(source.name_match)?.available === true) : null;
        const incomplete = !source && (!local || (local.available === false && store.pickup_enabled && sources.some(source => !find(source.name_match))));
        unknown ||= !local || incomplete;
        transfer ||= !!source;
        const stocked = local?.available === true;
        const name = local?.name || store.name_match.replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1));
        const row = this.clone('[data-pickup-store-row]');
        row.classList.add(stocked || source ? 'store-availability--available' : 'store-availability--unavailable');
        copy(row, '[data-name]', name);
        const badge = row.querySelector('[data-badge]');
        badge.textContent = stocked ? (this.options.inStockLabel || 'In stock') : source ? 'Available for transfer' : incomplete ? 'Availability unknown' : (this.options.outOfStockLabel || 'Out of stock');
        if (!incomplete) badge.classList.add('bikezone-pickup-badge--' + (stocked || source ? 'success' : 'error'));
        badge.hidden = this.options.showStockBadges === 'false';
        const timing = source ? source.transfer_time : stocked ? store.pickup_time : '';
        const pickup = !store.pickup_enabled ? 'Pickup not offered at this location' : incomplete ? 'Pickup availability unknown' : stocked || source
          ? 'Pickup available' + (timing ? ', ' + timing.charAt(0).toLowerCase() + timing.slice(1) : '') : 'Pickup currently unavailable';
        const transferNote = row.querySelector('[data-transfer-note]');
        transferNote.hidden = !source;
        transferNote.textContent = source ? 'Transfer from ' + find(source.name_match).name + ' required.' : '';
        copy(row, '[data-timing]', pickup);
        row.querySelector('[data-pickup-status]').hidden = !store.pickup_enabled;
        row.querySelector('[data-pickup-icon]').hidden = !(store.pickup_enabled && (stocked || source));
        // Use the destination's address, never the transfer source's address.
        const address = [local?.address, store.address, this.addresses[normalize(store.name_match)]]
          .find(address => address?.address1) || {};
        const province = address.province_code || ({Ontario:'ON',Quebec:'QC'})[address.province] || address.province;
        const lines = [address.address1,address.address2,[address.city,province,address.zip].filter(Boolean).join(' '),address.country];
        const addressNode = row.querySelector('[data-address]');
        if (!special(name) && this.options.showLocationAddress !== 'false') addressNode.textContent = lines.filter(Boolean).join('\n');
        if (!special(name) && address.phone && this.options.showLocationPhone !== 'false') {
          const phone = document.createElement('a');
          phone.href = 'tel:' + address.phone.replace(/[^\d+]/g,'');
          phone.textContent = address.phone;
          addressNode.append(document.createElement('br'),phone);
        }
        this.rows.append(row);
        // Preserve staging's compact stock-only summary. Transfers belong in the drawer.
        if (stocked) {
          const summary = this.clone('[data-pickup-summary-row]');
          copy(summary, '[data-name]', name);
          if (!special(name) && this.options.showLocationAddress !== 'false') {
            const text = lines.slice(0,3).filter(Boolean).join(' ');
            copy(summary, '[data-address]', text ? ': ' + text : '');
          }
          this.summary.append(summary);
        }
      }
      if (!this.summary.childElementCount) this.summary.textContent = transfer ? 'Transfer available. Check store details.' : unknown ? 'Availability incomplete. Check store details.' : 'Currently out of stock';
      else if (unknown) {
        const note = document.createElement('p');
        note.className = 'store-availability__small';
        note.textContent = 'Some store availability is unknown.';
        this.summary.append(note);
      }
    }

    async update(variant) {
      this.controller?.abort();
      const request = ++this.request;
      const id = variant?.id ? String(variant.id) : '';
      copy(this.drawer, '[data-pickup-variant]', variant?.title === 'Default Title' ? '' : variant?.title || '');
      if (this.image) {
        const media = variant?.featured_media?.preview_image || variant?.featured_image;
        const src = typeof media === 'string' ? media : media?.src || media?.url;
        const url = new URL(src || this.defaultImage, location.origin);
        url.searchParams.set('width','192');
        this.image.src = url.href;
        this.image.removeAttribute('srcset');
      }
      if (!id) { this.message('Unavailable combination. Choose available options.'); return; }
      if (!this.configured) { this.message('Availability unavailable. Please contact the store.'); return; }
      this.message('Checking availability…');
      const controller = this.controller = new AbortController();
      const timer = setTimeout(() => controller.abort(),12000);
      try {
        const url = new URL(this.url);
        url.searchParams.set('variant_id',id);
        const response = await fetch(url,{signal:controller.signal,headers:{Accept:'application/json'}});
        if (!response.ok) throw Error('Inventory request failed');
        const data = await response.json();
        if (request !== this.request || !this.isConnected) return;
        if (!data || data.ok === false || !Array.isArray(data.locations) || (data.variant_id && String(data.variant_id) !== id)) throw Error('Invalid inventory');
        this.render(data.locations);
      } catch (_) {
        if (request === this.request && this.isConnected) this.message('Availability could not be loaded. Please try another variant or contact the store.');
      } finally { clearTimeout(timer); }
    }

    open() {
      opened?.close();
      opened = this;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.drawer.inert = false;
      this.drawer.setAttribute('aria-hidden','false');
      this.overlay.setAttribute('aria-hidden','false');
      this.trigger.setAttribute('aria-expanded','true');
      this.drawer.querySelector('[data-pickup-close]').focus({preventScroll:true});
    }

    close() {
      if (opened !== this) return;
      opened = null;
      this.drawer.inert = true;
      this.drawer.setAttribute('aria-hidden','true');
      this.overlay.setAttribute('aria-hidden','true');
      this.trigger.setAttribute('aria-expanded','false');
      document.body.style.overflow = this.previousOverflow;
      if (this.trigger.isConnected) this.trigger.focus({preventScroll:true});
    }
  }

  // Capture prevents Terrain's parent modal from consuming the same Escape/Tab.
  window.addEventListener('keydown', event => {
    if (!opened) return;
    if (event.key === 'Escape') {
      escapeRelease = true;
      event.preventDefault(); event.stopImmediatePropagation(); opened.close();
    } else if (event.key === 'Tab') {
      const items = [...opened.drawer.querySelectorAll('button, a[href]')].filter(el => !el.disabled && !el.hidden);
      const first = items[0], last = items[items.length - 1];
      event.stopImmediatePropagation();
      if (!opened.drawer.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  },true);
  window.addEventListener('keyup', event => {
    if (event.key !== 'Escape' || !escapeRelease) return;
    escapeRelease = false;
    event.preventDefault(); event.stopImmediatePropagation();
  },true);
  window.addEventListener('focusin', event => {
    if (!opened) return;
    event.stopImmediatePropagation();
    if (!opened.drawer.contains(event.target)) opened.drawer.querySelector('[data-pickup-close]').focus();
  },true);
  document.addEventListener('modalClose', () => opened?.close());
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('bikezone-availability').forEach(element => element.disconnectedCallback());
  });
  customElements.define('bikezone-availability',Availability);
})();
